// src/components/JobForm.jsx

import React, { useState, useEffect } from "react";
import { getData, postData, putData } from "../../services/FetchNodeservices";
import { FaCloudUploadAlt } from "react-icons/fa";

export default function JobForm({ onClose, job }) {

  const [companies, setCompanies] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  const [formData, setFormData] = useState({
    reqId: "",
    company: "",
    jobTitle: "",
    companyWebsite: "",
    vacancies: "",
    experience: "",
    country: "",
    state: "",
    city: "",
    jobLocation: "",
    annualCtc: "",
    companyDescription: "",
    jobDescription: "",
    spocName: "",
    spocNumber: "",
    spocEmail: "",
    file: null
  });

  // ================= LOAD INITIAL DATA =================

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {

    const res1 = await getData("job/companies");
    if (res1?.status) setCompanies(res1.data || []);

    const res2 = await getData("job/countries");
    if (res2?.status) setCountries(res2.data || []);

  };

  // ================= EDIT PREFILL =================

  useEffect(() => {

    if (!job) return;

    const loadEditData = async () => {

      const countryId = job.country || "";
      const stateId = job.state || "";

      if (countryId) {
        const res = await getData(`job/states/${countryId}`);
        if (res.status) setStates(res.data || []);
      }

      if (stateId) {
        const res = await getData(`job/cities/${stateId}`);
        if (res.status) setCities(res.data || []);
      }

      setFormData({
        reqId: job.reqId || "",
        company: job.company || "",
        jobTitle: job.title || "",
        companyWebsite: job.companyWebsite || "",
        vacancies: job.vacancies || "",
        experience: job.experience || "",
        country: countryId,
        state: stateId,
        city: job.city || "",
        jobLocation: job.jobLocation || "",
        annualCtc: job.annualCtc || "",
        companyDescription: job.companyDescription || "",
        jobDescription: job.jobDescription || "",
        spocName: job.spocName || "",
        spocNumber: job.spocNumber || "",
        spocEmail: job.spocEmail || "",
        file: null
      });

    };

    loadEditData();

  }, [job]);

  // ================= COUNTRY CHANGE =================

  useEffect(() => {

    if (!formData.country) return;

    const loadStates = async () => {

      const res = await getData(`job/states/${formData.country}`);

      if (res?.status) {
        setStates(res.data || []);
      }

    };

    loadStates();

  }, [formData.country]);

  // ================= STATE CHANGE =================

  useEffect(() => {

    if (!formData.state) return;

    const loadCities = async () => {

      const res = await getData(`job/cities/${formData.state}`);

      if (res?.status) {
        setCities(res.data || []);
      }

    };

    loadCities();

  }, [formData.state]);

  // ================= COMPANY AUTO FILL =================

  const handleCompanySelect = (e) => {

    const companyId = e.target.value;

    const selectedCompany = companies.find(
      (c) => String(c.id) === String(companyId)
    );

    setFormData((prev) => ({
      ...prev,
      company: selectedCompany?.company_name || "",
      companyWebsite: selectedCompany?.company_website || "",
      companyDescription: selectedCompany?.describe_company || "",
      jobLocation: selectedCompany?.address || "",
      spocName: selectedCompany?.contact_person || "",
      spocNumber: selectedCompany?.mobile_number || "",
      spocEmail:
        selectedCompany?.email_id ||
        selectedCompany?.personal_email ||
        ""
    }));

  };

  // ================= INPUT CHANGE =================

  const handleChange = (e) => {

    const { name, value, files } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: files ? files[0] : value
    }));

  };

  // ================= SUBMIT =================

  const handleSubmit = async (e) => {

    e.preventDefault();

    const data = new FormData();

    Object.keys(formData).forEach((k) => {
      data.append(k, formData[k] ?? "");
    });

    const user = JSON.parse(localStorage.getItem("USER"));

    if (user) {
      data.append("user_id", user.id);
      data.append("user_name", user.name);
    }

    let res;

    if (job) {
      res = await putData(`job/update-job/${formData.reqId}`, data);
    } else {
      res = await postData("job/submit-job", data);
    }

    if (res?.status) {

      alert(job ? "Job Updated Successfully!" : "Job Posted Successfully!");

      onClose?.();

    } else {

      alert("Error: " + (res.message || "Unknown"));

    }

  };

  return (

    <div className="max-h-[80vh] overflow-auto">

      <form onSubmit={handleSubmit} className="p-4">

        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl font-semibold">
            {job ? "Update Job" : "Job Post"}
          </h2>

          <button type="button" onClick={onClose} className="text-gray-500">
            ✕
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">

          {/* Company */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Select Company
            </label>

            <select
              value={
                companies.find((c) => c.company_name === formData.company)?.id || ""
              }
              onChange={handleCompanySelect}
              required
              className="w-full border rounded px-3 py-2"
            >
              <option value="">Select Company</option>

              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.company_name}
                </option>
              ))}
            </select>
          </div>

          {/* Job Title */}
          <div>
            <label className="block text-sm font-medium mb-1">Job Title</label>

            <input
              name="jobTitle"
              value={formData.jobTitle}
              onChange={handleChange}
              placeholder="Ex: Senior React Developer"
              required
              className="w-full border rounded px-3 py-2"
            />
          </div>

          {/* Website */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Company Website
            </label>

            <input
              type="url"
              name="companyWebsite"
              value={formData.companyWebsite}
              onChange={handleChange}
              placeholder="https://company.com"
              className="w-full border rounded px-3 py-2"
            />
          </div>

          {/* Vacancies */}
          <div>
            <label className="block text-sm font-medium mb-1">Vacancies</label>

            <input
              type="number"
              name="vacancies"
              value={formData.vacancies}
              onChange={handleChange}
              placeholder="Ex: 3"
              required
              className="w-full border rounded px-3 py-2"
            />
          </div>

          {/* Experience */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Experience
            </label>

            <input
              name="experience"
              value={formData.experience}
              onChange={handleChange}
              placeholder="Ex: 3-6 Years"
              required
              className="w-full border rounded px-3 py-2"
            />
          </div>

          {/* Job Location */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Company Location
            </label>

            <input
              name="jobLocation"
              value={formData.jobLocation}
              onChange={handleChange}
              placeholder="Ex: Bangalore / Remote"
              required
              className="w-full border rounded px-3 py-2"
            />
          </div>

          {/* Annual CTC */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Annual CTC
            </label>

            <input
              name="annualCtc"
              value={formData.annualCtc}
              onChange={handleChange}
              placeholder="Ex: 1200000"
              className="w-full border rounded px-3 py-2"
            />
          </div>

          {/* Country */}
<div>
  <label className="block text-sm font-medium mb-1">
    Country
  </label>

  <select
    name="country"
    value={formData.country}
    onChange={handleChange}
    className="w-full border rounded px-3 py-2"
  >
    <option value="">Select Country</option>

    {countries.map((c) => (
      <option key={c.id} value={c.id}>
        {c.name}
      </option>
    ))}
  </select>
</div>

{/* State */}
<div>
  <label className="block text-sm font-medium mb-1">
    State
  </label>

  <select
    name="state"
    value={formData.state}
    onChange={handleChange}
    className="w-full border rounded px-3 py-2"
  >
    <option value="">Select State</option>

    {states.map((s) => (
      <option key={s.id} value={s.id}>
        {s.name}
      </option>
    ))}
  </select>
</div>

{/* City */}
<div>
  <label className="block text-sm font-medium mb-1">
    City
  </label>

  <select
    name="city"
    value={formData.city}
    onChange={handleChange}
    className="w-full border rounded px-3 py-2"
  >
    <option value="">Select City</option>

    {cities.map((c) => (
      <option key={c.id} value={c.id}>
        {c.name}
      </option>
    ))}
  </select>
</div>

          {/* Company Description */}
          <div className="md:col-span-2">
            <textarea
              name="companyDescription"
              rows={3}
              value={formData.companyDescription}
              onChange={handleChange}
              placeholder="Short company overview..."
              className="w-full border rounded px-3 py-2"
            />
          </div>

          {/* Job Description */}
          <div className="md:col-span-2">
            <textarea
              name="jobDescription"
              rows={3}
              value={formData.jobDescription}
              onChange={handleChange}
              placeholder="Detailed job description..."
              className="w-full border rounded px-3 py-2"
            />
          </div>

          {/* SPOC */}
          <input
            name="spocName"
            value={formData.spocName}
            onChange={handleChange}
            placeholder="SPOC Name"
            className="border rounded px-3 py-2"
          />

          <input
            name="spocNumber"
            value={formData.spocNumber}
            onChange={handleChange}
            placeholder="SPOC Phone"
            className="border rounded px-3 py-2"
          />

          <input
            name="spocEmail"
            value={formData.spocEmail}
            onChange={handleChange}
            placeholder="SPOC Email"
            className="border rounded px-3 py-2"
          />

          {/* Upload */}
          <div className="md:col-span-2">
            <label className="flex items-center gap-2 border px-3 py-2 rounded cursor-pointer">
              <FaCloudUploadAlt /> Upload JD (Optional)

              <input
                type="file"
                name="file"
                onChange={handleChange}
                className="hidden"
              />
            </label>
          </div>

          {/* Buttons */}
          <div className="md:col-span-2 flex justify-end gap-3">

            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded"
            >
              Cancel
            </button>

            <button
              type="submit"
              className="px-4 py-2 rounded bg-gradient-to-r from-cyan-400 to-blue-600 text-white"
            >
              {job ? "Update Job" : "Submit"}
            </button>

          </div>

        </div>

      </form>

    </div>

  );
}